import React, { useState, useEffect, useContext } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import './Modal.css';

const UserFormModal = ({ isOpen, user, onClose, onSubmit }) => {
  const { user: currentUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    document_id: '',
    password: '',
    role: 'user',
    profile_id: '',
    transito_id: '',
    concesionario_id: '',
    punto_venta_id: '',
    can_view_cupl: false,
    can_view_liquidacion: false,
    repartidor_transitos: [],
    concesionarios_adicionales: [],
  });
  const [profiles, setProfiles] = useState([]);
  const [transitos, setTransitos] = useState([]);
  const [concesionarios, setConcesionarios] = useState([]);
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
      fetchTransitos();
      fetchConcesionarios();
    }
  }, [isOpen]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get('/api/profiles');
      setProfiles(response.data);
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const fetchTransitos = async () => {
    try {
      const response = await axios.get('/api/transitos');
      setTransitos(response.data);
    } catch (err) {
      console.error('Error fetching transitos:', err);
    }
  };

  const fetchConcesionarios = async () => {
    try {
      const response = await axios.get('/api/concesionarios');
      setConcesionarios(response.data);
    } catch (err) {
      console.error('Error fetching concesionarios:', err);
    }
  };

  useEffect(() => {
    if (formData.concesionario_id) {
      axios.get('/api/puntos-venta')
        .then(res => setPuntosVenta(res.data.filter(pv => pv.concesionario_id == formData.concesionario_id)))
        .catch(err => console.error(err));
    } else {
      setPuntosVenta([]);
    }
  }, [formData.concesionario_id]);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name || '',
        document_id: user.document_id || '',
        password: '',
        role: user.role,
        profile_id: user.profile_id || '',
        transito_id: user.transito_id || '',
        concesionario_id: user.concesionario_id || '',
        punto_venta_id: user.punto_venta_id || '',
        can_view_cupl: user.can_view_cupl || false,
        can_view_liquidacion: user.can_view_liquidacion || false,
        repartidor_transitos: user.repartidor_transitos || [],
        concesionarios_adicionales: (user.concesionarios_adicionales || []).filter(Boolean),
      });
    } else {
      setFormData({
        username: '',
        email: '',
        full_name: '',
        document_id: '',
        password: '',
        role: 'user',
        profile_id: '',
        transito_id: '',
        concesionario_id: '',
        punto_venta_id: '',
        can_view_cupl: false,
        can_view_liquidacion: false,
        repartidor_transitos: [],
        concesionarios_adicionales: [],
      });
    }
    setError('');
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.email || !formData.document_id) {
      setError('Username, email y cédula son requeridos');
      return;
    }

    if (!user && !formData.password) {
      setError('La contraseña es requerida para nuevos usuarios');
      return;
    }

    if (profiles.find(p => p.id == formData.profile_id)?.name === 'administrativo_concesionario' && !formData.concesionario_id) {
      setError('El perfil Administrativo Concesionario requiere un concesionario principal');
      return;
    }

    setLoading(true);

    const selectedProfile = profiles.find(p => p.id == formData.profile_id);
    const assignedRole = selectedProfile ? selectedProfile.name : (formData.role || 'user');

    const data = {
      username: formData.username,
      email: formData.email,
      full_name: formData.full_name,
      document_id: formData.document_id,
      role: assignedRole,
      profile_id: formData.profile_id || null,
      transito_id: formData.transito_id || null,
      concesionario_id: formData.concesionario_id || null,
      punto_venta_id: formData.punto_venta_id || null,
      can_view_cupl: formData.can_view_cupl,
      can_view_liquidacion: formData.can_view_liquidacion,
      repartidor_transitos: formData.repartidor_transitos,
      concesionarios_adicionales: formData.concesionarios_adicionales,
    };
 
    if (formData.password) {
      data.password = formData.password;
    }

    try {
      await onSubmit(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{user ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="username">Usuario:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Nombre de usuario"
              disabled={loading || !!user}
              className={user ? 'disabled-input' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="correo@ejemplo.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="full_name">Nombre Completo:</label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Nombre completo"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="document_id">Cédula (Obligatoria):</label>
            <input
              type="text"
              id="document_id"
              name="document_id"
              value={formData.document_id}
              onChange={handleChange}
              placeholder="Número de cédula"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Contraseña: {user && '(Dejar en blanco para no cambiar)'}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={user ? 'Dejar en blanco para mantener actual' : 'Contraseña'}
              disabled={loading}
            />
          </div>



          <div className="form-group">
            <label htmlFor="profile_id">Perfil (Módulos):</label>
            <select
              id="profile_id"
              name="profile_id"
              value={formData.profile_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Ninguno (Sin módulos específicos)</option>
              {profiles
                .filter(p => currentUser?.role === 'superadmin' || p.name !== 'superadmin')
                .map(p => (
                  <option key={p.id} value={p.id}>🛡️ {p.name}</option>
                ))}
            </select>
          </div>

          {/* Mostrar Tránsito solo si no es admin de concesionario y NO es repartidor */}
          {!currentUser?.concesionario_id && profiles.find(p => p.id == formData.profile_id)?.name !== 'repartidor' && (
            <div className="form-group">
              <label htmlFor="transito_id">Empresa (Tránsito):</label>
              <select
                id="transito_id"
                name="transito_id"
                value={formData.transito_id}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value) {
                    setFormData(prev => ({ ...prev, concesionario_id: '' }));
                  }
                }}
                disabled={loading || (formData.concesionario_id !== '') || !!currentUser?.transito_id}
              >
                <option value="">Ninguna (Global o Concesionario)</option>
                {transitos.map(t => (
                  <option key={t.id} value={t.id}>🏢 {t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Selector múltiple para Repartidor */}
          {!currentUser?.concesionario_id && profiles.find(p => p.id == formData.profile_id)?.name === 'repartidor' && (
            <div className="form-group">
              <label>Tránsitos Asignados (Repartidor):</label>
              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px', backgroundColor: '#fff' }}>
                {transitos.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      id={`transito_${t.id}`}
                      checked={formData.repartidor_transitos.includes(t.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData(prev => {
                          const newTransitos = checked 
                            ? [...prev.repartidor_transitos, t.id]
                            : prev.repartidor_transitos.filter(id => id !== t.id);
                          return { ...prev, repartidor_transitos: newTransitos, transito_id: '' };
                        });
                      }}
                      disabled={loading}
                      style={{ width: '18px', height: '18px', margin: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor={`transito_${t.id}`} style={{ margin: '0 0 0 8px', fontWeight: 'normal', cursor: 'pointer', userSelect: 'none' }}>
                      🏢 {t.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mostrar Concesionario solo si no es admin de tránsito */}
          {!currentUser?.transito_id && profiles.find(p => p.id == formData.profile_id)?.name !== 'repartidor' && (
            <div className="form-group">
              <label htmlFor="concesionario_id">Concesionario (Tramitador):</label>
              <select
                id="concesionario_id"
                name="concesionario_id"
                value={formData.concesionario_id}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value) {
                    setFormData(prev => ({ ...prev, transito_id: '' }));
                  }
                }}
                disabled={loading || (formData.transito_id !== '') || !!currentUser?.concesionario_id}
              >
                <option value="">Ninguno (Global o Tránsito)</option>
                {concesionarios.map(c => (
                  <option key={c.id} value={c.id}>🏪 {c.name}</option>
                ))}
              </select>
              {formData.transito_id && <small style={{color: '#64748b'}}>Un usuario no puede pertenecer a un Tránsito y un Concesionario a la vez.</small>}
            </div>
          )}

          {/* Concesionarios adicionales: solo perfil administrativo_concesionario */}
          {currentUser?.role === 'superadmin' && formData.concesionario_id && profiles.find(p => p.id == formData.profile_id)?.name === 'administrativo_concesionario' && (
            <div className="form-group">
              <label>Concesionarios adicionales (además del principal):</label>
              <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px', backgroundColor: '#fff' }}>
                {concesionarios.filter(c => String(c.id) !== String(formData.concesionario_id)).map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      id={`conc_extra_${c.id}`}
                      checked={formData.concesionarios_adicionales.map(String).includes(String(c.id))}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          concesionarios_adicionales: checked
                            ? [...prev.concesionarios_adicionales, c.id]
                            : prev.concesionarios_adicionales.filter(id => String(id) !== String(c.id))
                        }));
                      }}
                      disabled={loading}
                      style={{ width: '18px', height: '18px', margin: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor={`conc_extra_${c.id}`} style={{ margin: '0 0 0 8px', fontWeight: 'normal', cursor: 'pointer' }}>🏪 {c.name}</label>
                  </div>
                ))}
              </div>
              <small style={{ color: '#64748b' }}>El usuario verá en solo lectura el concesionario principal y los marcados aquí.</small>
            </div>
          )}

          {/* Selector de Punto de Venta */}
          {formData.concesionario_id && (
            <div className="form-group">
              <label htmlFor="punto_venta_id">Punto de Venta (Sucursal):</label>
              <select
                id="punto_venta_id"
                name="punto_venta_id"
                value={formData.punto_venta_id}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">Ninguno (Global del Concesionario)</option>
                {puntosVenta.map(pv => (
                  <option key={pv.id} value={pv.id}>🏪 {pv.name} {pv.city ? `(${pv.city})` : ''}</option>
                ))}
              </select>
            </div>
          )}
 
          {/* Checkboxes de permisos (solo superadmin) */}
          {currentUser?.role === 'superadmin' && (
            <>
              <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px', marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  id="can_view_cupl"
                  name="can_view_cupl"
                  checked={formData.can_view_cupl}
                  onChange={(e) => setFormData(prev => ({ ...prev, can_view_cupl: e.target.checked }))}
                  disabled={loading}
                  style={{ width: '20px', height: '20px', margin: 0, cursor: 'pointer' }}
                />
                <label htmlFor="can_view_cupl" style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer', userSelect: 'none' }}>
                  Visualizar número de CUPL en trámites (para Concesionarios)
                </label>
              </div>
              <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  id="can_view_liquidacion"
                  name="can_view_liquidacion"
                  checked={formData.can_view_liquidacion}
                  onChange={(e) => setFormData(prev => ({ ...prev, can_view_liquidacion: e.target.checked }))}
                  disabled={loading}
                  style={{ width: '20px', height: '20px', margin: 0, cursor: 'pointer' }}
                />
                <label htmlFor="can_view_liquidacion" style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer', userSelect: 'none' }}>
                  Visualizar archivo de Liquidación (para Concesionarios)
                </label>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading}
            >
              {loading ? '⏳ Guardando...' : user ? '✓ Actualizar' : '✓ Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
